import {Component, OnInit, OnDestroy} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Sidenavaction} from '../../../../../assets/js/application/sidenavaction';
import {BarCodeService} from '../../../../services/barCode.service';
import BarCodeModel from '../../../../services/barCode.model';
import {BusinessEntityService} from '../../../../services/businessEntity.service';
import BusinessEntity from '../../../../services/businessEntity.model';
import BarcodeModel from '../../../../services/barCode.model';
import {ToastrService} from 'ngx-toastr';
import { CommonMethodsService } from '../../../../services/commonMethods.service';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';

/**
 * BarCodeComponent
 *
 * This component is responsible for managing Barcodes within the Admin Panel.
 * It provides a user interface for administrators to view, create, update, and search for barcodes.
 * The component handles barcode sequence generation, validation, and assignment to business entities.
 *
 * Key Responsibilities:
 * - Fetch and display a list of all barcodes associated with a business.
 * - Provide search functionality to filter businesses for barcode assignment.
 * - Manage the creation of new barcodes with validation for sequence and format.
 * - Handle updates to existing barcodes.
 * - Validate barcode sequences and ranges.
 * - Manage UI state for loading indicators, form visibility, and submission status.
 *
 * Features:
 * - **CRUD Operations**: Create and Update barcodes.
 * - **Sequence Validation**: Ensures barcode sequences are valid and contiguous.
 * - **Business Association**: Assigns barcodes to specific business entities.
 * - **Form Validation**: Ensures barcode formats follow specific patterns (alphanumeric, length).
 * - **Role-based Data Loading**: Loads businesses based on user role (Admin vs Provider).
 *
 * Data Flow:
 * 1. Component initializes (`ngOnInit`).
 * 2. Fetches business entities based on user role.
 * 3. User selects a business, triggering `getAlladdBarCodeWithCustomer`.
 * 4. Fetches barcodes for the selected business.
 * 5. User interactions (add/edit) trigger state updates and service calls.
 * 6. `addBarcode` sends data to backend and refreshes the list on success.
 *
 * @component
 * @selector app-bar-code
 * @example
 * <app-bar-code></app-bar-code>
 *
 * @implements {OnInit}
 * @implements {OnDestroy}
 * @author Generated by CompDoc
 * @version 1.0.0
 */
@Component({
  selector: 'app-bar-code',
  templateUrl: './bar-code.component.html',
  styleUrls: ['./bar-code.component.scss']
})
export class BarCodeComponent implements OnInit, OnDestroy {
  /**
   * Model for the current barcode being added or edited.
   * @type {BarCodeModel}
   */
  public barcode: BarCodeModel;

  /**
   * Array holding all fetched barcodes for the selected business.
   * @type {Array<BarCodeModel>}
   */
  public barcodes: Array<BarCodeModel>;

  /**
   * Array of business entities available for selection.
   * @type {Array<BusinessEntity>}
   */
  public businessEntities: Array<BusinessEntity>;

  /**
   * Reactive form group for barcode details.
   * @type {FormGroup}
   */
  addDeviceForm: FormGroup;

  /**
   * Flag indicating if the form has been submitted.
   * @type {boolean}
   * @default false
   */
  submitted = false;

  /**
   * Flag indicating if the page is currently loading data.
   * @type {boolean}
   * @default false
   */
  pgLoad = false;

  /**
   * Role of the logged-in user.
   * @type {string}
   */
  public loginPersonType: string;

  /**
   * Keyword used for filtering/searching.
   * @type {string}
   */
  public keyword: string;

  /**
   * Generic item property.
   * @type {any}
   */
  public item: any;

  /**
   * Flag to prevent double clicks on add barcode action.
   * @type {boolean}
   */
  public addBarcodeDoubleClick: boolean;

  /**
   * Flag for business dropdown open/close state.
   * @type {boolean}
   * @default false
   */
  public isDropdownOpen = false;

  /**
   * Filtered options for business dropdown.
   * @type {any[]}
   */
  public filteredOptions: any[] = [];

  /**
   * Placeholder text for business search input.
   * @type {string}
   * @default 'Enter the business name'
   */
  public businessSearch: string = 'Enter the business name';

  /**
   * Flag indicating if the form is in 'Update' mode.
   * @type {boolean}
   */
  public updateButton: boolean;

  /**
   * Flag indicating if the form is in 'Create' mode.
   * @type {boolean}
   */
  public submitButton: boolean;

  /**
   * Original numeric part of the barcode (for update logic).
   * @type {any}
   */
  public originalNumericPart: any;

  /**
   * Original first part of the barcode (for update logic).
   * @type {string}
   */
  public originalFirst: string;

  /**
   * Original 'From' barcode value (for update logic).
   * @type {any}
   */
  public originalBarcodeFrom: any;

  /**
   * Original 'To' barcode value (for update logic).
   * @type {any}
   */
  public originalBarcodeTo: any;

  /**
   * Constructor for BarCodeComponent
   *
   * Initializes the component, injects required services, and sets up the reactive form.
   *
   * Form Validation Rules:
   * - businessId: Required.
   * - barcodeCount: Required, Min 2, Max 4, Numeric pattern (excluding '00').
   * - barcodeFrom: Required, Length 17, Alphanumeric pattern.
   * - barcodeTo: Required, Length 17, Alphanumeric pattern.
   * - barcodeSequence: Required, Length 12, Alphanumeric pattern.
   *
   * @constructor
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @service ToastrService
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @service FormBuilder
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @service Sidenavaction
   * @param {BarCodeService} barcodeService - Service for Barcode API operations.
   * @service BarCodeService
   * @param {BusinessEntityService} businessEntityService - Service for Business Entity API operations.
   * @service BusinessEntityService
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @service CommonMethodsService
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @service LocationStrategy
   * @param {PreviousRouteService} previousRouteService - Service for tracking navigation history.
   * @service PreviousRouteService
   */
  constructor(private toasterService: ToastrService, public formBuilder: FormBuilder, public sidenavaction: Sidenavaction, public barcodeService: BarCodeService, public businessEntityService: BusinessEntityService, public commonMethodsService: CommonMethodsService,
    private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.barcode = new BarCodeModel();
    /*form Validation*/
    this.addDeviceForm = this.formBuilder.group({
      businessId: ['', Validators.required],
      barcodeCount: ['', [Validators.required,
        Validators.minLength(2),
        Validators.maxLength(4),
        Validators.pattern('^(?!00)[0-9]{2,4}$')
      ]],
      barcodeFrom: ['', [Validators.required,
        Validators.minLength(17),
        Validators.maxLength(17),
        Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$')
      ]],
      barcodeTo: ['', [Validators.required,
        Validators.minLength(17),
        Validators.maxLength(17),
        Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$')
      ]],
      barcodeSequence: ['', [Validators.required,
        Validators.minLength(12),
        Validators.maxLength(12),
        Validators.pattern('^(?=.*[0-9])(?=.*[a-zA-Z])([a-zA-Z0-9]+)$')
      ]]
    });
  }

  /**
   * Angular lifecycle hook - Component initialization
   *
   * Performs the following initialization steps:
   * 1. Tracks the current route.
   * 2. Initializes component state flags.
   * 3. Retrieves user role from local storage.
   * 4. Loads business entities based on user role (Admin vs Provider).
   * 5. Filters business entities to exclude specific types if necessary.
   *
   * @async
   * @method ngOnInit
   * @returns {Promise<void>}
   * @lifecycle Angular component initialization hook
   */
  public async ngOnInit() {
    try {
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.keyword = 'businessName';
      this.submitButton = true;
      this.addBarcodeDoubleClick = false;
      this.loginPersonType = localStorage.getItem('loginPersonType');
      if (this.loginPersonType == 'Business Admin') {
        this.businessSearch = undefined;
        this.addDeviceForm.get('businessId').patchValue('');
        this.businessEntities = await this.businessEntityService.getAllfarmerByProviderID(localStorage.businessID);
        this.filteredOptions = this.businessEntities;
      } else {
        this.businessSearch = undefined;
        this.addDeviceForm.get('businessId').patchValue('');
        this.businessEntities = await this.businessEntityService.getAllBusiness();
        if (this.businessEntities && this.businessEntities.length > 0) {
          this.businessEntities = this.businessEntities.filter(businessEntity => (businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID
            != '618a18ac0f339c001e991b08') && (businessEntity.businessType && businessEntity.businessType.length > 0 && businessEntity.businessType[0].businessTypeID && businessEntity.businessType[0].businessTypeID
            != '62b3fa8998be51001d17a310'));
        }
        this.filteredOptions = this.businessEntities;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
    this.barcodes = [];
  }

  /**
   * Angular lifecycle hook - Component destruction
   *
   * Cleans up resources:
   * - Cancels ongoing HTTP requests for BusinessEntityService.
   *
   * @method ngOnDestroy
   * @returns {void}
   * @lifecycle Angular component destruction hook
   */
  public ngOnDestroy(): void {
    this.businessEntityService.cancelOnGoingHttpRequests();
  }

  /**
   * Getter for easy access to form controls in the template.
   *
   * @method barcodeFormValidation
   * @returns {AbstractControl} Form controls
   */
  get barcodeFormValidation() {
    return this.addDeviceForm.controls;
  }

  /**
   * Filters business options based on search text.
   *
   * - Filters by business name (case-insensitive).
   * - Updates `filteredOptions`.
   *
   * @method filterBusinesses
   * @returns {void}
   */
  public filterBusinesses(): void {
    const value = (this.addDeviceForm.get('businessId')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * Handles business selection from dropdown.
   * Sets the selected business ID and fetches associated barcodes.
   *
   * @method selectOption
   * @param {BusinessEntity} business - The selected business entity.
   * @returns {void}
   */
  public selectOption(business: BusinessEntity): void {
    this.addDeviceForm.get('businessId')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.barcode.business_id = business.id;
    if (this.barcode.business_id) {
      this.getAlladdBarCodeWithCustomer(this.barcode.business_id);
    }
  }

  /**
   * Fetches all barcodes associated with a specific business ID.
   *
   * @async
   * @method getAlladdBarCodeWithCustomer
   * @param {string} businessId - The ID of the business.
   * @returns {Promise<void>}
   */
  public async getAlladdBarCodeWithCustomer(businessId: string) {
    try {
      this.barcodes = await this.barcodeService.getBarcodeByBusinessId(businessId);
      this.pgLoad = true;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * Handles the creation of a new barcode.
   *
   * - Validates the form and barcode sequence logic.
   * - Checks if 'From' and 'To' barcodes match the sequence.
   * - Validates numeric range and count.
   * - Calls `addBarcode` service method.
   * - Refreshes the list on success.
   *
   * @async
   * @method addBarcode
   * @returns {Promise<void | boolean>}
   */
  async addBarcode() {
    this.submitted = true;
    this.addDeviceForm.markAllAsTouched();
    // stop here if form is invalid
    if (this.addDeviceForm.invalid) {
      return;
    }
    if (!this.barcode.barcode_from.startsWith(this.barcode.barcode_sequence) || !this.barcode.barcode_to.startsWith(this.barcode.barcode_sequence)) {
      this.toasterService.clear();
      this.toasterService.error('Barcode sequence missing in Barcode From or Barcode To', '', {
        timeOut: 2000
      });
      return false;
    }
    if (this.barcode.barcode_count === '01') {
      if (this.barcode.barcode_from !== this.barcode.barcode_to) {
        this.toasterService.clear();
        this.toasterService.error('Barcode From and Barcode To should be the same', '', {
          timeOut: 2000
        });
        return false;
      }
    } else {
      const fromNumericPart = parseInt(this.barcode.barcode_from.slice(-4));
      const toNumericPart = parseInt(this.barcode.barcode_to.slice(-4));
      const count = parseInt(this.barcode.barcode_count, 10);
      if (count <= 0 || isNaN(count) || (toNumericPart - fromNumericPart) !== count - 1) {
        this.toasterService.clear();
        this.toasterService.error('Invalid Barcode Count and Range', '', {
          timeOut: 2000
        });
        return false;
      }
    }
    if (this.barcode.barcode_from && this.barcode.barcode_to) {
      this.barcode.barcode_sequence = this.barcode.barcode_from.slice(0, 12);
    }
    try {
      this.addBarcodeDoubleClick = true;
      this.barcode = await this.barcodeService.addBarcode(this.barcode);
      this.toasterService.success('Barcode added successfully');
      this.addBarcodeDoubleClick = false;
      await this.getAlladdBarCodeWithCustomer(this.barcode.business_id);
      this.pgLoad = true;
      this.submitted = false;
      this.submitButton = true;
      this.updateButton = false;
      this.barcode = new BarCodeModel();
      this.addDeviceForm.reset();
      this.submitted = false;    // Reset validation flag
      this.addDeviceForm.markAsPristine(); // optional - clears dirty flags
      this.addDeviceForm.markAsUntouched(); // optional - clears touched state
      this.businessSearch = undefined;
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addBarcodeDoubleClick = false;
    }
  }

  /**
   * Updates the barcode sequence based on the 'From' barcode value.
   * Extracts the first 12 characters as the sequence.
   *
   * @method barcodeSequence
   * @returns {void}
   */
  public barcodeSequence() {
    this.barcode.barcode_sequence = this.barcode.barcode_from.slice(0, 12);
  }

  /**
   * Retrieves barcode details by ID for editing.
   * Populates the form and sets up update mode.
   *
   * @async
   * @method getBarcodeDetails
   * @param {string} id - The ID of the barcode to edit.
   * @returns {Promise<BarCodeModel>} The barcode object.
   */
  public async getBarcodeDetails(id) {
    this.updateButton = true;
    this.submitButton = false;
    // Find the barcode by id
    this.barcode = this.barcodes.find(pondcode => pondcode && pondcode.id === id);
    this.originalBarcodeFrom = this.barcode.barcode_from;
    this.originalBarcodeTo = this.barcode.barcode_to;
    // Extract the numeric part from the 5th character to the end of the string
    this.originalNumericPart = this.barcode.barcode_to.slice(4, 17);
    // Extract the first 3 characters and preserve the letter case
    this.originalFirst = this.barcode.barcode_to.slice(0, 4);
    // Find the business entity by id
    let businessEntity = this.filteredOptions.find(business => business.id == this.barcode.business_id);
    // Call the selectOption function with the business entity
    this.selectOption(businessEntity);
    return this.barcode;
  }

  /**
   * Updates the barcode sequence and range based on the count.
   * Automatically calculates the new 'From' and 'To' values when count changes.
   *
   * @method updateBarcodeSequence
   * @returns {void}
   */
  public updateBarcodeSequence() {
    // Ensure barcode_count is a valid number and originalBarcodeTo is set
    if (!isNaN(Number(this.barcode.barcode_count)) && this.originalBarcodeTo) {
      const count = +this.barcode.barcode_count; // Get the new barcode count

      // Extract the numeric part from barcode_to (assuming the numeric part starts from the 9th character)
      const barcodeToNumericPart = parseInt(this.originalBarcodeTo.slice(8), 10);

      // Update barcode_from by incrementing the numeric part of barcode_to by 1
      const newBarcodeFromNumericPart = (barcodeToNumericPart + 1).toString().padStart(8, '0'); // Increment and pad
      this.barcode.barcode_from = `${this.originalBarcodeTo.slice(0, 8)}${newBarcodeFromNumericPart}`; // Set new barcode_from

      // Update barcode_to by adding barcode_count to the numeric part of barcode_to
      const newBarcodeToNumericPart = (barcodeToNumericPart + count).toString().padStart(8, '0'); // Add count and pad
      this.barcode.barcode_to = `${this.originalBarcodeTo.slice(0, 8)}${newBarcodeToNumericPart}`; // Set new barcode_to
    }
  }

  /**
   * Trigger for count change event.
   * Calls `updateBarcodeSequence`.
   *
   * @method changeCount
   * @returns {void}
   */
  public changeCount() {
    this.updateBarcodeSequence();
  }

  /**
   * Handles the update of an existing barcode.
   *
   * - Validates the form and sequence logic.
   * - Calls `updateOneByID` service method.
   * - Refreshes the list on success.
   *
   * @async
   * @method updateBarcode
   * @returns {Promise<void | any>}
   */
  public async updateBarcode() {
    this.submitted = true;
    // stop here if form is invalid
    if (this.addDeviceForm.invalid) {
      return;
    }
    if (!this.barcode.barcode_from.startsWith(this.barcode.barcode_sequence) || !this.barcode.barcode_to.startsWith(this.barcode.barcode_sequence)) {
      this.toasterService.clear();
      return this.toasterService.error('Barcode sequence missing in Barcode From or Barcode To', '', {

      });
    }
    if (this.barcode.barcode_count === '01') {
      if (this.barcode.barcode_from !== this.barcode.barcode_to) {
        this.toasterService.clear();
        this.toasterService.error('Barcode From and Barcode To should be the same');
      }
    } else {
      const fromNumericPart = parseInt(this.barcode.barcode_from.slice(-4));
      const toNumericPart = parseInt(this.barcode.barcode_to.slice(-4));
      const count = parseInt(this.barcode.barcode_count, 10);

      if (count <= 0 || isNaN(count) || (toNumericPart - fromNumericPart) !== count - 1) {
        this.toasterService.clear();
        this.toasterService.error('Invalid Barcode Count and Range');
      }
    }
    try {
      this.addBarcodeDoubleClick = true;
      const updateBarcode = Object.assign({}, this.barcode);
      delete updateBarcode.id;
      await this.barcodeService.updateOneByID(this.barcode.id, updateBarcode);
      this.toasterService.success('Barcode updated successfully');
      this.addBarcodeDoubleClick = false;
      this.pgLoad = true;
      this.submitted = false;
      this.submitButton = true;
      this.updateButton = false;
      this.barcode = new BarCodeModel();
      this.businessSearch = undefined;
      this.originalBarcodeFrom = undefined;
      this.originalBarcodeTo = undefined;
      this.cancel();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
      this.addBarcodeDoubleClick = false;
    }
  }

  /**
   * Resets the form and component state to default (Cancel action).
   *
   * @method cancel
   * @returns {void}
   */
  public cancel(): void {
    this.submitted = false;
    this.submitButton = true;
    this.updateButton = false;
    this.addBarcodeDoubleClick = false;
    this.barcode = new BarcodeModel();
    this.addDeviceForm.reset();
    this.barcodes = [];
    this.businessSearch = undefined;
  }
}
